import { Capacitor } from '@capacitor/core'
import { NativeBiometric } from '@capgo/capacitor-native-biometric'
import { useCallback } from 'react'

export interface SessionCredentials {
  username: string
  accessToken: string
  refreshToken: string
}

export const useSessionCredentials = () => {
  const SERVER_URL = import.meta.env.VITE_HEALTHBLOCKS_API || ''

  const saveCredentials = useCallback(
    async (userId: string, accessToken: string, refreshToken: string) => {
      // Skip biometric credential saving on web platform
      if (!Capacitor.isNativePlatform()) {
        return
      }

      try {
        await NativeBiometric.setCredentials({
          username: userId,
          password: `${accessToken}+${refreshToken}`,
          server: SERVER_URL,
        })
      } catch (error) {
        console.error('Failed to save credentials', error)
      }
    },
    [SERVER_URL],
  )

  const getCredentials = useCallback(async (): Promise<SessionCredentials | null> => {
    // Skip biometric credential retrieval on web platform
    if (!Capacitor.isNativePlatform()) {
      return null
    }

    try {
      const credentials = await NativeBiometric.getCredentials({
        server: SERVER_URL,
      })

      if (credentials && credentials.password) {
        const [accessToken, refreshToken] = credentials.password.split('+')
        return {
          username: credentials.username,
          accessToken,
          refreshToken,
        }
      }
      return null
    } catch {
      return null
    }
  }, [SERVER_URL])

  const clearCredentials = useCallback(async () => {
    // Skip biometric credential deletion on web platform
    if (!Capacitor.isNativePlatform()) {
      return
    }

    try {
      await NativeBiometric.deleteCredentials({
        server: SERVER_URL,
      })
    } catch (error) {
      console.error('Failed to clear credentials', error)
    }
  }, [SERVER_URL])

  return {
    saveCredentials,
    getCredentials,
    clearCredentials,
  }
}
